import { SNAP_INTERVAL_MINUTES, MIN_BLOCK_HEIGHT_PERCENT, MINUTES_PER_DAY, MIN_DURATION_MINUTES } from "./constants";
import type { Schedule } from "./types";

// --- Time Conversion ---

// Convert "HH:MM" string to minutes since midnight (0-1439)
export const timeToMinutes = (time: string): number => {
  const parts = time.split(":").map(Number);
  return (parts[0] ?? 0) * 60 + (parts[1] ?? 0);
};

// Convert minutes since midnight to "HH:MM" string
export const minutesToTime = (minutes: number): string => {
  const clamped = Math.max(0, Math.min(MINUTES_PER_DAY - 1, minutes));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
};

// --- Display Formatting ---

// Format "HH:MM" to human-readable "9AM" or "9:30AM"
export const formatTime = (time: string): string => {
  const mins = timeToMinutes(time);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const period = h >= 12 ? "PM" : "AM";
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${hour}${period}` : `${hour}:${m.toString().padStart(2, "0")}${period}`;
};

// Format hour number to "12AM", "6PM", etc.
export const formatHour = (h: number): string => {
  if (h === 0 || h === 24) return "12AM";
  if (h === 12) return "12PM";
  return h > 12 ? `${h - 12}PM` : `${h}AM`;
};

// --- Grid Positioning ---

// Calculate block position as CSS percentages for absolute positioning (vertical layout)
export const getBlockPosition = (startTime: string, endTime: string) => {
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  const top = (start / MINUTES_PER_DAY) * 100;
  const height = ((end - start) / MINUTES_PER_DAY) * 100;
  return {
    top: `${top}%`,
    height: `${Math.max(height, MIN_BLOCK_HEIGHT_PERCENT)}%`,
  };
};

// Calculate block position for horizontal layout (time on X-axis)
export const getBlockPositionHorizontal = (startTime: string, endTime: string) => {
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  const left = (start / MINUTES_PER_DAY) * 100;
  const width = ((end - start) / MINUTES_PER_DAY) * 100;
  return {
    left: `${left}%`,
    width: `${Math.max(width, MIN_BLOCK_HEIGHT_PERCENT)}%`,
  };
};

// Convert pixel X position in grid to minutes (for horizontal layout)
export const pixelToMinutesHorizontal = (pixelX: number, gridWidth: number): number => {
  const ratio = Math.max(0, Math.min(1, pixelX / gridWidth));
  return snapToGrid(ratio * MINUTES_PER_DAY);
};

// Snap minutes to nearest grid interval for clean dragging
export const snapToGrid = (minutes: number): number => {
  return Math.round(minutes / SNAP_INTERVAL_MINUTES) * SNAP_INTERVAL_MINUTES;
};

// Convert pixel Y position in grid to minutes
export const pixelToMinutes = (pixelY: number, gridHeight: number): number => {
  const ratio = Math.max(0, Math.min(1, pixelY / gridHeight));
  return snapToGrid(ratio * MINUTES_PER_DAY);
};

// --- ID Generation ---

export const generateId = (): string => {
  return `sched-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
};

// --- Validation ---

// Validate and clamp a new start time during resize
export const clampStartTime = (newStartMinutes: number, endMinutes: number): number => {
  const maxStart = endMinutes - MIN_DURATION_MINUTES;
  return Math.max(0, Math.min(newStartMinutes, maxStart));
};

// Validate and clamp a new end time during resize
export const clampEndTime = (newEndMinutes: number, startMinutes: number): number => {
  const minEnd = startMinutes + MIN_DURATION_MINUTES;
  return Math.max(minEnd, Math.min(MINUTES_PER_DAY - 1, newEndMinutes));
};

// Clamp a new start position during drag (keeps duration)
export const clampDragPosition = (newStartMinutes: number, durationMinutes: number): number => {
  const maxStart = MINUTES_PER_DAY - durationMinutes;
  return Math.max(0, Math.min(newStartMinutes, maxStart));
};

// --- Datetime Utilities ---

// Extract just the date part (YYYY-MM-DD) from a datetime string like "2025-05-01T08:00" or "2025-05-01T08:00:00Z"
export const extractDatePart = (datetimeStr: string): string => {
  return datetimeStr.split("T")[0] ?? datetimeStr;
};

// Strip the Z suffix and seconds from a datetime string (for use with datetime-local inputs)
// "2025-05-01T08:00:00Z" → "2025-05-01T08:00"
// "2025-05-01T08:00" → "2025-05-01T08:00" (unchanged)
export const stripUtcSuffix = (datetimeStr: string): string => {
  return datetimeStr.replace(/Z$/, "").replace(/(\d{2}:\d{2}):\d{2}$/, "$1");
};

// Check if a datetime string is in UTC mode (has Z suffix)
export const isUtcDatetime = (datetimeStr: string): boolean => {
  return datetimeStr.endsWith("Z");
};

// Format an ISO datetime string for display (e.g., "Dec 18, 2024 8:00 PM")
export const formatDateTimeShort = (datetimeStr: string): string => {
  const datePart = extractDatePart(datetimeStr);
  const timePart = datetimeStr.split("T")[1];
  const dateDisplay = new Date(datePart + "T00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  if (!timePart) return dateDisplay;
  const cleanTime = timePart.replace("Z", "");
  const timeDisplay = formatTime(cleanTime.slice(0, 5));
  return `${dateDisplay} ${timeDisplay}`;
};

// --- Date Utilities ---

// Get Monday of the week containing the given date
export const getWeekStart = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  // Convert Sunday (0) to 7, so Monday becomes day 1
  const diff = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Add days to a date
export const addDays = (date: Date, days: number): Date => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

// Format date range for display (e.g., "Dec 16 - 22, 2024" or "Dec 30 - Jan 5, 2025")
export const formatDateRange = (weekStart: Date): string => {
  const weekEnd = addDays(weekStart, 6);
  const startMonth = weekStart.toLocaleDateString("en-US", { month: "short" });
  const endMonth = weekEnd.toLocaleDateString("en-US", { month: "short" });
  const startDay = weekStart.getDate();
  const endDay = weekEnd.getDate();
  const startYear = weekStart.getFullYear();
  const endYear = weekEnd.getFullYear();

  if (startYear !== endYear) {
    return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
  }
  if (startMonth !== endMonth) {
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`;
  }
  return `${startMonth} ${startDay} - ${endDay}, ${endYear}`;
};

// Format a single date for day column header (e.g., "16")
export const formatDayDate = (weekStart: Date, dayIndex: number): string => {
  const date = addDays(weekStart, dayIndex);
  return date.getDate().toString();
};

// Get ISO date string (YYYY-MM-DD) for a specific day in the week
export const getDateForDay = (weekStart: Date, dayIndex: number): string => {
  const date = addDays(weekStart, dayIndex);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Check if a date/datetime string falls within a week (extracts date part if datetime)
export const isDateInWeek = (dateStr: string, weekStart: Date): boolean => {
  const datePart = extractDatePart(dateStr);
  const date = new Date(datePart);
  const weekEnd = addDays(weekStart, 6);
  weekEnd.setHours(23, 59, 59, 999);
  return date >= weekStart && date <= weekEnd;
};

// Check if two date ranges overlap
export const doRangesOverlap = (start1: Date, end1: Date, start2: Date, end2: Date): boolean => {
  return start1 <= end2 && end1 >= start2;
};

// Get the start and end dates (ISO strings) for the week containing the given date
export const getWeekBounds = (date: Date): { start: string; end: string } => {
  const weekStart = getWeekStart(date);
  const weekEnd = addDays(weekStart, 6);
  return {
    start: weekStart.toISOString().split("T")[0] ?? "",
    end: weekEnd.toISOString().split("T")[0] ?? "",
  };
};

// Format ISO date/datetime string for display (e.g., "Dec 18, 2024")
export const formatDateShort = (dateStr: string): string => {
  const datePart = extractDatePart(dateStr);
  const date = new Date(datePart + "T00:00");
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// --- Overlap Layout ---

type OverlapLayoutItem = { column: number; totalColumns: number };

// Calculate layout for overlapping schedules - assigns each to a row for vertical stacking
export const calculateOverlapLayout = (schedules: Schedule[]): Map<string, OverlapLayoutItem> => {
  const result = new Map<string, OverlapLayoutItem>();
  if (schedules.length === 0) return result;

  const sorted = [...schedules].sort((a, b) => {
    return timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
  });

  // Find overlapping groups
  const groups: Schedule[][] = [];
  let currentGroup: Schedule[] = [];

  for (const schedule of sorted) {
    const start = timeToMinutes(schedule.startTime);
    const end = timeToMinutes(schedule.endTime);

    // Check if this schedule overlaps with any in current group
    const overlapsWithGroup = currentGroup.some((s) => {
      const sStart = timeToMinutes(s.startTime);
      const sEnd = timeToMinutes(s.endTime);
      return start < sEnd && end > sStart;
    });

    if (overlapsWithGroup || currentGroup.length === 0) {
      currentGroup.push(schedule);
    } else {
      if (currentGroup.length > 0) groups.push(currentGroup);
      currentGroup = [schedule];
    }
  }
  if (currentGroup.length > 0) groups.push(currentGroup);

  for (const group of groups) {
    const totalColumns = group.length;
    group.forEach((schedule, idx) => {
      result.set(schedule.id, { column: idx, totalColumns });
    });
  }

  return result;
};
